import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { ReactNode } from 'react'
import { APP_ENV } from '@/shared/config/env'
import { authApi, type LoginPayload } from '@/shared/api'
import { ApiError, NetworkError, UnauthorizedError } from '@/shared/api'
import type { AuthBundle, AuthState, AuthUser } from './types'

// Hard ceiling for the initial /auth/me call. If the backend is offline or
// the network hangs, we drop to LoginScreen with a connection hint instead
// of leaving the user stranded on the boot spinner.
const BOOT_TIMEOUT_MS = 10000

interface AuthContextValue extends AuthState {
  login: (payload: LoginPayload) => Promise<void>
  logout: () => Promise<void>
  refresh: () => Promise<void>
  clearError: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

const INITIAL_STATE: AuthState = {
  status: 'booting',
  user: null,
  rawUser: null,
  organization: null,
  preferences: null,
  role: null,
  session: null,
  error: null,
}

function computeInitials(fullName: string, email: string): string {
  const name = fullName.trim()
  if (name.length > 0) {
    const parts = name.split(/\s+/).filter(Boolean)
    if (parts.length === 1) return parts[0]!.slice(0, 1).toUpperCase()
    return (parts[0]!.charAt(0) + parts[parts.length - 1]!.charAt(0)).toUpperCase()
  }
  return email.charAt(0).toUpperCase() || '?'
}

function bundleToState(bundle: AuthBundle): AuthState {
  const user: AuthUser = {
    id: bundle.user.id,
    email: bundle.user.email,
    full_name: bundle.user.full_name,
    display_name: bundle.user.display_name,
    avatar_url: bundle.user.avatar_url,
    initials: computeInitials(bundle.user.full_name, bundle.user.email),
  }
  return {
    status: 'authenticated',
    user,
    rawUser: bundle.user,
    organization: bundle.organization,
    preferences: bundle.preferences,
    role: bundle.role,
    session: bundle.session,
    error: null,
  }
}

interface ProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: ProviderProps) {
  const [state, setState] = useState<AuthState>(INITIAL_STATE)
  const bootedRef = useRef(false)

  const hydrate = useCallback(async (signal?: AbortSignal, opts?: { timedOut?: () => boolean }) => {
    try {
      const bundle = await authApi.me(signal)
      setState(bundleToState(bundle))
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        if (opts?.timedOut?.()) {
          setState({
            ...INITIAL_STATE,
            status: 'unauthenticated',
            error: `Não foi possível verificar a sessão. Confirme se o backend está rodando em ${APP_ENV.apiBaseUrl}.`,
          })
        }
        return
      }
      if (err instanceof UnauthorizedError) {
        setState({ ...INITIAL_STATE, status: 'unauthenticated' })
        return
      }
      if (err instanceof NetworkError) {
        setState({
          ...INITIAL_STATE,
          status: 'unauthenticated',
          error: `Não foi possível conectar ao backend em ${APP_ENV.apiBaseUrl}. Verifique se ele está rodando.`,
        })
        return
      }
      const message =
        err instanceof ApiError
          ? err.detail
          : err instanceof Error
            ? err.message
            : 'Falha ao verificar sessão.'
      setState({
        ...INITIAL_STATE,
        status: 'unauthenticated',
        error: message,
      })
    }
  }, [])

  useEffect(() => {
    // Boot exactly once per provider lifetime. StrictMode mounts effects
    // twice in dev; if we aborted the in-flight request on cleanup we'd
    // cancel the only attempt and the app would sit on "Verificando sessão…"
    // forever. The timeout below is the only thing allowed to abort boot.
    if (bootedRef.current) return
    bootedRef.current = true

    const controller = new AbortController()
    let timedOut = false
    const timeoutId = window.setTimeout(() => {
      timedOut = true
      controller.abort()
    }, BOOT_TIMEOUT_MS)
    void hydrate(controller.signal, { timedOut: () => timedOut }).finally(() => {
      window.clearTimeout(timeoutId)
    })
  }, [hydrate])

  const login = useCallback(async (payload: LoginPayload) => {
    setState((prev) => ({ ...prev, error: null }))
    try {
      const bundle = await authApi.login(payload)
      setState(bundleToState(bundle))
    } catch (err) {
      const message =
        err instanceof UnauthorizedError
          ? 'Credenciais inválidas.'
          : err instanceof ApiError
            ? err.detail
            : err instanceof Error
              ? err.message
              : 'Falha ao entrar.'
      setState((prev) => ({ ...prev, status: 'unauthenticated', error: message }))
      throw err
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      await authApi.logout()
    } catch {
      // Logout is idempotent server-side; ignore failures and reset state.
    }
    setState({ ...INITIAL_STATE, status: 'unauthenticated' })
  }, [])

  const refresh = useCallback(async () => {
    await hydrate()
  }, [hydrate])

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }))
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      login,
      logout,
      refresh,
      clearError,
    }),
    [state, login, logout, refresh, clearError],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>')
  }
  return ctx
}
